<script setup lang="ts">
import Simplebar from "simplebar-vue";
import { ref } from "vue";

import SimpleButton from "@/components/ui/SimpleButton.vue";

const props = defineProps<{
  contentWidth?: string;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "open"): void;
}>();

const isOpen = ref(false);

function close() {
  isOpen.value = false;
  emit("close");
}

function open() {
  isOpen.value = true;
  emit("open");
}

defineExpose({
  open,
  close,
});
</script>

<template>
  <Teleport to="body">
    <Transition name="drawer-appear">
      <div v-if="isOpen" class="side-drawer">
        <div class="outside-click-catcher" @click="close" />

        <div
          class="side-drawer-content"
          :style="{ width: props.contentWidth ?? 'auto', maxWidth: '100dvw' }"
        >
          <header>
            <div class="title">
              <slot name="title"></slot>
            </div>
            <div class="close">
              <SimpleButton icon-left="icon-close" kind="ghost" @click="close" />
            </div>
          </header>

          <Simplebar class="scroller">
            <slot />
          </Simplebar>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.side-drawer {
  position: fixed;
  z-index: 8000;
  top: 0;
  left: 0;
  display: flex;
  width: 100dvw;
  height: 100dvh;
  justify-content: flex-end;
  background-color: rgb(from var(--color-gray-900) r g b / 30%);
  transition: background-color var(--animation-duration) var(--animation-easing);

  .outside-click-catcher {
    position: absolute;
    z-index: 1;
    width: 100%;
    height: 100%;
  }

  .side-drawer-content {
    z-index: 2;
    display: flex;
    overflow: hidden;
    height: calc(100dvh - (var(--numbers-16) * 2));
    min-height: 0;
    flex-direction: column;
    padding: var(--numbers-32) var(--numbers-32) 0 var(--numbers-32);
    border-radius: var(--numbers-16);
    margin: var(--numbers-16) var(--numbers-16) var(--numbers-16) 0;
    background-color: var(--background-primary);
    gap: var(--numbers-16);
    transition: transform var(--animation-duration) var(--animation-easing);

    header {
      display: flex;
      align-items: center;
      justify-content: space-between;

      :deep(.skore-button) {
        --height: var(--numbers-12);

        border: none;
        font-size: var(--numbers-10);
      }
    }

    .scroller {
      min-height: 0;
      flex: 1 1 auto;

      /* Keep Simplebar sized to the available space */
      :deep(.simplebar-wrapper),
      :deep(.simplebar-content-wrapper) {
        height: 100%;
      }
    }
  }
}

.drawer-appear-enter-from,
.drawer-appear-leave-to {
  background-color: transparent;

  .side-drawer-content {
    transform: translateX(100%);
  }
}
</style>
